import { getSupabaseClient } from '@/integrations/supabase/client';
import dayjs from 'dayjs';
import { type ClientSalesReport } from '@/types';

export const clientSalesService = {
  /**
   * Get sales aggregated by saved client (customer_name) for a given organization and date range.
   * Matches sales records against saved clients (name or identification) and enforces RBAC.
   * 
   * @param organizationId - Organization to query
   * @param from - Start date (inclusive)
   * @param to - End date (inclusive)
   * @param userRole - Current user's role for RBAC enforcement (admin or manager)
   * @returns Array of client sales reports
   */
  async getClientSalesReport(
    organizationId: string,
    from: Date,
    to: Date,
    userRole?: string
  ): Promise<ClientSalesReport[]> {
    // Enforce RBAC: only admin and manager can access client sales reports
    if (userRole !== 'admin' && userRole !== 'manager') {
      throw new Error('Insufficient permissions: admin or manager role required');
    }

    const supabase = getSupabaseClient();

    // Query saved clients for the organization with id
    const { data: clients, error: clientsError } = await supabase
      .from('clients')
      .select('id, name, identification')
      .eq('organization_id', organizationId)
      .order('name', { ascending: true });

    if (clientsError) throw clientsError;
    if (!clients || clients.length === 0) return [];

    // Build map of client identifiers to client info using client ID as canonical key
    const clientInfoMap = new Map<string, { id: string; name: string; identification: string }>();
    const nameToIdMap = new Map<string, string>();
    const identificationToIdMap = new Map<string, string>();
    
    clients.forEach(client => {
      const clientKey = client.id;
      const clientInfo = { 
        id: client.id, 
        name: client.name, 
        identification: client.identification ?? '' 
      };
      
      clientInfoMap.set(clientKey, clientInfo);
      nameToIdMap.set(client.name, clientKey);
      if (client.identification) {
        identificationToIdMap.set(client.identification, clientKey);
      }
    });

    // Normalize date range to start/end of day
    const fromDate = dayjs(from).startOf('day').toISOString();
    const toDate = dayjs(to).endOf('day').toISOString();

    // Query sales for the organization and date range
    const { data: sales, error: salesError } = await supabase
      .from('sales')
      .select('customer_name, total, sale_date')
      .eq('organization_id', organizationId)
      .gte('sale_date', fromDate)
      .lte('sale_date', toDate);

    if (salesError) throw salesError;
    if (!sales || sales.length === 0) return [];

    // Aggregate sales by canonical client ID
    const aggMap = new Map<string, { total: number; count: number; lastDate: Date }>();
    
    sales.forEach(sale => {
      // Try to match by name first, then by identification
      let clientId = nameToIdMap.get(sale.customer_name);
      if (!clientId) {
        clientId = identificationToIdMap.get(sale.customer_name);
      }
      
      // Skip sales that don't match any saved client
      if (!clientId) return;
      
      const existing = aggMap.get(clientId) || { total: 0, count: 0, lastDate: new Date(0) };
      existing.total += Number(sale.total || 0);
      existing.count += 1;
      const saleDate = new Date(sale.sale_date);
      if (saleDate > existing.lastDate) {
        existing.lastDate = saleDate;
      }
      aggMap.set(clientId, existing);
    });

    if (aggMap.size === 0) return [];

    // Convert to report items using canonical client info
    const report: ClientSalesReport[] = Array.from(aggMap.entries())
      .map(([clientId, stats]) => {
        const clientInfo = clientInfoMap.get(clientId)!;
        return {
          clientName: clientInfo.name,
          identification: clientInfo.identification,
          totalSales: stats.total,
          transactionCount: stats.count,
          lastPurchaseDate: stats.lastDate,
        };
      })
      .sort((a, b) => b.totalSales - a.totalSales);

    return report;
  },
};